import { Graphics } from 'pixi.js';
import type { EntityId, SimState } from '../../sim/state';
import type { RenderContext } from '../context';
import type { EnemyPose } from '../enemies';
import type { Frame } from '../frame';
import { type BlankCircle, createBlankFilter } from './blank-filter';
import { DRAIN, drain, returnRings } from './faux-contact-moments';

const PAUSE = { width: 3.5, height: 10, gap: 3, above: 14 };

interface Blank {
  readonly byId: EntityId;
  readonly startTick: number;
  readonly endTick: number;
  endedAt?: number;
}

export interface Since {
  readonly start?: number;
  readonly end?: number;
}

export interface BlankShow {
  // First in a frame; a level offer suspends the blank.
  update(state: SimState, frame: Frame): void;
  since(id: EntityId, frame: Frame): Since;
  reset(): void;
  destroy(): void;
}

export function createBlankShow(
  ctx: RenderContext,
  poseOf: (id: EntityId) => EnemyPose | undefined,
): BlankShow {
  const { layers } = ctx;
  const marks = layers.veil.addChild(new Graphics());
  const filter = createBlankFilter(layers.world, DRAIN.dark);
  let blank: Blank | null = null;

  function sinceOf(frame: Frame): Since {
    if (blank === null) {
      return {};
    }
    const { ticksPerBeat } = frame.tempo;
    const end = blank.endedAt ?? (frame.now >= blank.endTick ? blank.endTick : undefined);
    return {
      start: (frame.now - blank.startTick) / ticksPerBeat,
      ...(end === undefined ? {} : { end: (frame.now - end) / ticksPerBeat }),
    };
  }

  function drawBlank(state: SimState, frame: Frame): void {
    const since = sinceOf(frame);
    if (since.start === undefined) {
      filter.off(layers.setting);
      return;
    }
    const shown = drain(since.start, since.end, frame.calm);
    if (shown === undefined) {
      filter.off(layers.setting);
      if (since.end !== undefined) {
        blank = null;
      }
      return;
    }
    const { core } = state;
    const boss = blank === null ? undefined : poseOf(blank.byId);
    const circle: BlankCircle =
      shown.reveal !== undefined
        ? { center: core, share: shown.reveal, inside: false }
        : shown.spread !== undefined
          ? { center: boss?.sprite.position ?? core, share: shown.spread, inside: true }
          : { center: core, share: 0, inside: false };
    const far = filter.set(layers.setting, shown.amount, circle, frame);
    if (since.end === undefined && since.start >= 0) {
      for (const trap of state.traps) {
        const top = trap.y - PAUSE.above - PAUSE.height;
        for (const side of [-1, 1]) {
          const x = trap.x + side * (PAUSE.gap / 2 + PAUSE.width / 2) - PAUSE.width / 2;
          marks.rect(x, top, PAUSE.width, PAUSE.height);
        }
      }
      marks.fill({ color: frame.palette.texte, alpha: 0.9 });
    }
    if (since.end !== undefined && !frame.calm) {
      const colors = [frame.palette.healer, frame.palette.noyau];
      returnRings(since.end).forEach((ring, index) => {
        marks.circle(core.x, core.y, core.radius + ring.share * far).stroke({
          width: index === 0 ? 4 : 2,
          color: colors[index] ?? 0xffffff,
          alpha: ring.alpha,
        });
      });
    }
  }

  return {
    update(state, frame): void {
      marks.clear();
      const { blackout } = state;
      if (blackout !== undefined) {
        if (blank?.startTick !== blackout.startTick) {
          blank = { ...blackout };
        }
      } else if (blank !== null && blank.endedAt === undefined) {
        blank.endedAt = Math.min(blank.endTick, state.tick);
      }
      if (state.status === 'running') {
        drawBlank(state, frame);
      } else {
        filter.off(layers.setting);
      }
    },
    since(id, frame): Since {
      return blank?.byId === id ? sinceOf(frame) : {};
    },
    reset(): void {
      blank = null;
      filter.off(layers.setting);
      marks.clear();
    },
    destroy(): void {
      filter.off(layers.setting);
      filter.destroy();
      marks.destroy();
    },
  };
}
